/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CancelWorkflowExecutionV2PathParameters,
  CreateWorkflowExecutionV2PathParameters,
  DownloadExecutionFileV2Params,
  DownloadExecutionFileV2PathParameters,
  ExportWorkflowExecutionNodeOutputsV2Params,
  ExportWorkflowExecutionNodeOutputsV2PathParameters,
  ExportWorkflowExecutionOutputsV2Params,
  ExportWorkflowExecutionOutputsV2PathParameters,
  GetWorkflowExecutionV2PathParameters,
  ListRecentWorkflowExecutionsV2Params,
  ListWorkflowExecutionsV2Params,
  ListWorkflowExecutionsV2PathParameters,
  PreviewExecutionFileV2Params,
  PreviewExecutionFileV2PathParameters,
  StreamWorkflowExecutionV2PathParameters,
  SubmitWorkflowApprovalDecisionV2PathParameters,
  TestExecuteWorkflowNodeV2PathParameters,
  TLApprovalDecisionRequest,
  TLDownloadUrlResponse,
  TLExecuteWorkflowRequest,
  TLHTTPValidationError,
  TLPaginatedExecutions,
  TLPaginatedRecentExecutions,
  TLPreviewResponse,
  TLTestExecuteNodeRequest,
  TLWorkflowExecution,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * List the most recent executions across every workflow the caller can access.
 * @summary List Recent Workflow Executions V2
 */
export const listRecentWorkflowExecutionsV2 = (
  params?: ListRecentWorkflowExecutionsV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedRecentExecutions>(
    { url: `/api/v2/workflow-executions`, method: 'GET', params, signal },
    options,
  );
};

export const getListRecentWorkflowExecutionsV2QueryKey = (params?: ListRecentWorkflowExecutionsV2Params) => {
  return ['api', 'v2', 'workflow-executions', ...(params ? [params] : [])] as const;
};

export const getListRecentWorkflowExecutionsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListRecentWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListRecentWorkflowExecutionsV2QueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>> = ({ signal }) =>
    listRecentWorkflowExecutionsV2(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListRecentWorkflowExecutionsV2QueryResult = NonNullable<
  Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>
>;
export type ListRecentWorkflowExecutionsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useListRecentWorkflowExecutionsV2<
  TData = Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListRecentWorkflowExecutionsV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
          TError,
          Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRecentWorkflowExecutionsV2<
  TData = Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListRecentWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
          TError,
          Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRecentWorkflowExecutionsV2<
  TData = Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListRecentWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Recent Workflow Executions V2
 */

export function useListRecentWorkflowExecutionsV2<
  TData = Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListRecentWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListRecentWorkflowExecutionsV2QueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Recent Workflow Executions V2
 */
export const prefetchListRecentWorkflowExecutionsV2Query = async <
  TData = Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListRecentWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listRecentWorkflowExecutionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListRecentWorkflowExecutionsV2QueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Recent Workflow Executions V2
 */
export const invalidateListRecentWorkflowExecutionsV2 = async (
  queryClient: QueryClient,
  params?: ListRecentWorkflowExecutionsV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListRecentWorkflowExecutionsV2QueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary List Workflow Executions V2
 */
export const listWorkflowExecutionsV2 = (
  { workflowId }: ListWorkflowExecutionsV2PathParameters,
  params?: ListWorkflowExecutionsV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedExecutions>(
    { url: `/api/v2/workflows/${workflowId}/executions`, method: 'GET', params, signal },
    options,
  );
};

export const getListWorkflowExecutionsV2QueryKey = (
  { workflowId }: ListWorkflowExecutionsV2PathParameters,
  params?: ListWorkflowExecutionsV2Params,
) => {
  return ['api', 'v2', 'workflows', workflowId, 'executions', ...(params ? [params] : [])] as const;
};

export const getListWorkflowExecutionsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof listWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: ListWorkflowExecutionsV2PathParameters,
  params?: ListWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListWorkflowExecutionsV2QueryKey({ workflowId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>> = ({ signal }) =>
    listWorkflowExecutionsV2({ workflowId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListWorkflowExecutionsV2QueryResult = NonNullable<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>>;
export type ListWorkflowExecutionsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useListWorkflowExecutionsV2<
  TData = Awaited<ReturnType<typeof listWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListWorkflowExecutionsV2PathParameters,
  params: undefined | ListWorkflowExecutionsV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkflowExecutionsV2>>,
          TError,
          Awaited<ReturnType<typeof listWorkflowExecutionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorkflowExecutionsV2<
  TData = Awaited<ReturnType<typeof listWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListWorkflowExecutionsV2PathParameters,
  params?: ListWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkflowExecutionsV2>>,
          TError,
          Awaited<ReturnType<typeof listWorkflowExecutionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorkflowExecutionsV2<
  TData = Awaited<ReturnType<typeof listWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListWorkflowExecutionsV2PathParameters,
  params?: ListWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Workflow Executions V2
 */

export function useListWorkflowExecutionsV2<
  TData = Awaited<ReturnType<typeof listWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: ListWorkflowExecutionsV2PathParameters,
  params?: ListWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListWorkflowExecutionsV2QueryOptions({ workflowId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Workflow Executions V2
 */
export const prefetchListWorkflowExecutionsV2Query = async <
  TData = Awaited<ReturnType<typeof listWorkflowExecutionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: ListWorkflowExecutionsV2PathParameters,
  params?: ListWorkflowExecutionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowExecutionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListWorkflowExecutionsV2QueryOptions({ workflowId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Workflow Executions V2
 */
export const invalidateListWorkflowExecutionsV2 = async (
  queryClient: QueryClient,
  { workflowId }: ListWorkflowExecutionsV2PathParameters,
  params?: ListWorkflowExecutionsV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListWorkflowExecutionsV2QueryKey({ workflowId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create Workflow Execution V2
 */
export const createWorkflowExecutionV2 = (
  { workflowId }: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest: BodyType<TLExecuteWorkflowRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowExecution>(
    {
      url: `/api/v2/workflows/${workflowId}/executions`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLExecuteWorkflowRequest,
      signal,
    },
    options,
  );
};

export const getCreateWorkflowExecutionV2QueryKey = (
  { workflowId }: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest?: BodyType<TLExecuteWorkflowRequest>,
) => {
  return ['POST', 'api', 'v2', 'workflows', workflowId, 'executions', tLExecuteWorkflowRequest] as const;
};

export const getCreateWorkflowExecutionV2QueryOptions = <
  TData = Awaited<ReturnType<typeof createWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest: BodyType<TLExecuteWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateWorkflowExecutionV2QueryKey({ workflowId }, tLExecuteWorkflowRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createWorkflowExecutionV2>>> = ({ signal }) =>
    createWorkflowExecutionV2({ workflowId }, tLExecuteWorkflowRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createWorkflowExecutionV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateWorkflowExecutionV2QueryResult = NonNullable<Awaited<ReturnType<typeof createWorkflowExecutionV2>>>;
export type CreateWorkflowExecutionV2QueryError = ErrorType<TLHTTPValidationError>;

export function useCreateWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof createWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest: BodyType<TLExecuteWorkflowRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowExecutionV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createWorkflowExecutionV2>>,
          TError,
          Awaited<ReturnType<typeof createWorkflowExecutionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof createWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest: BodyType<TLExecuteWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowExecutionV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createWorkflowExecutionV2>>,
          TError,
          Awaited<ReturnType<typeof createWorkflowExecutionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof createWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest: BodyType<TLExecuteWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Workflow Execution V2
 */

export function useCreateWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof createWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest: BodyType<TLExecuteWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateWorkflowExecutionV2QueryOptions({ workflowId }, tLExecuteWorkflowRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Workflow Execution V2
 */
export const prefetchCreateWorkflowExecutionV2Query = async <
  TData = Awaited<ReturnType<typeof createWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest: BodyType<TLExecuteWorkflowRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateWorkflowExecutionV2QueryOptions({ workflowId }, tLExecuteWorkflowRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Workflow Execution V2
 */
export const invalidateCreateWorkflowExecutionV2 = async (
  queryClient: QueryClient,
  { workflowId }: CreateWorkflowExecutionV2PathParameters,
  tLExecuteWorkflowRequest: BodyType<TLExecuteWorkflowRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateWorkflowExecutionV2QueryKey({ workflowId }, tLExecuteWorkflowRequest) },
    options,
  );

  return queryClient;
};

/**
 * Run one saved workflow node in isolation.
 *
 * The execution is persisted (so the standard get / stream / preview / download endpoints
 * work against it) but hidden from the list endpoints via ``is_test=True``. The node's
 * activity must be flagged ``safe_for_test_execution=True`` on its ``@node_activity``
 * decorator — only read-only / non-destructive activities are eligible.
 * @summary Test Execute Workflow Node V2
 */
export const testExecuteWorkflowNodeV2 = (
  { workflowId }: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest: BodyType<TLTestExecuteNodeRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowExecution>(
    {
      url: `/api/v2/workflows/${workflowId}/executions/test`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLTestExecuteNodeRequest,
      signal,
    },
    options,
  );
};

export const getTestExecuteWorkflowNodeV2QueryKey = (
  { workflowId }: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest?: BodyType<TLTestExecuteNodeRequest>,
) => {
  return ['POST', 'api', 'v2', 'workflows', workflowId, 'executions', 'test', tLTestExecuteNodeRequest] as const;
};

export const getTestExecuteWorkflowNodeV2QueryOptions = <
  TData = Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest: BodyType<TLTestExecuteNodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getTestExecuteWorkflowNodeV2QueryKey({ workflowId }, tLTestExecuteNodeRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>> = ({ signal }) =>
    testExecuteWorkflowNodeV2({ workflowId }, tLTestExecuteNodeRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type TestExecuteWorkflowNodeV2QueryResult = NonNullable<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>>;
export type TestExecuteWorkflowNodeV2QueryError = ErrorType<TLHTTPValidationError>;

export function useTestExecuteWorkflowNodeV2<
  TData = Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest: BodyType<TLTestExecuteNodeRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>,
          TError,
          Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTestExecuteWorkflowNodeV2<
  TData = Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest: BodyType<TLTestExecuteNodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>,
          TError,
          Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTestExecuteWorkflowNodeV2<
  TData = Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest: BodyType<TLTestExecuteNodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Test Execute Workflow Node V2
 */

export function useTestExecuteWorkflowNodeV2<
  TData = Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest: BodyType<TLTestExecuteNodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getTestExecuteWorkflowNodeV2QueryOptions({ workflowId }, tLTestExecuteNodeRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Test Execute Workflow Node V2
 */
export const prefetchTestExecuteWorkflowNodeV2Query = async <
  TData = Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest: BodyType<TLTestExecuteNodeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof testExecuteWorkflowNodeV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getTestExecuteWorkflowNodeV2QueryOptions({ workflowId }, tLTestExecuteNodeRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Test Execute Workflow Node V2
 */
export const invalidateTestExecuteWorkflowNodeV2 = async (
  queryClient: QueryClient,
  { workflowId }: TestExecuteWorkflowNodeV2PathParameters,
  tLTestExecuteNodeRequest: BodyType<TLTestExecuteNodeRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getTestExecuteWorkflowNodeV2QueryKey({ workflowId }, tLTestExecuteNodeRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Workflow Execution V2
 */
export const getWorkflowExecutionV2 = (
  { workflowId, executionId }: GetWorkflowExecutionV2PathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowExecution>(
    { url: `/api/v2/workflows/${workflowId}/executions/${executionId}`, method: 'GET', signal },
    options,
  );
};

export const getGetWorkflowExecutionV2QueryKey = ({
  workflowId,
  executionId,
}: GetWorkflowExecutionV2PathParameters) => {
  return ['api', 'v2', 'workflows', workflowId, 'executions', executionId] as const;
};

export const getGetWorkflowExecutionV2QueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId }: GetWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWorkflowExecutionV2QueryKey({ workflowId, executionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowExecutionV2>>> = ({ signal }) =>
    getWorkflowExecutionV2({ workflowId, executionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined && executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getWorkflowExecutionV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetWorkflowExecutionV2QueryResult = NonNullable<Awaited<ReturnType<typeof getWorkflowExecutionV2>>>;
export type GetWorkflowExecutionV2QueryError = ErrorType<TLHTTPValidationError>;

export function useGetWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof getWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowExecutionV2PathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowExecutionV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowExecutionV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowExecutionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof getWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowExecutionV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowExecutionV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowExecutionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof getWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Workflow Execution V2
 */

export function useGetWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof getWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId }: GetWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWorkflowExecutionV2QueryOptions({ workflowId, executionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Workflow Execution V2
 */
export const prefetchGetWorkflowExecutionV2Query = async <
  TData = Awaited<ReturnType<typeof getWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, executionId }: GetWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkflowExecutionV2QueryOptions({ workflowId, executionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Workflow Execution V2
 */
export const invalidateGetWorkflowExecutionV2 = async (
  queryClient: QueryClient,
  { workflowId, executionId }: GetWorkflowExecutionV2PathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetWorkflowExecutionV2QueryKey({ workflowId, executionId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Cancel Workflow Execution V2
 */
export const cancelWorkflowExecutionV2 = (
  { workflowId, executionId }: CancelWorkflowExecutionV2PathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowExecution>(
    { url: `/api/v2/workflows/${workflowId}/executions/${executionId}/cancel`, method: 'POST', signal },
    options,
  );
};

export const getCancelWorkflowExecutionV2QueryKey = ({
  workflowId,
  executionId,
}: CancelWorkflowExecutionV2PathParameters) => {
  return ['POST', 'api', 'v2', 'workflows', workflowId, 'executions', executionId, 'cancel'] as const;
};

export const getCancelWorkflowExecutionV2QueryOptions = <
  TData = Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId }: CancelWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCancelWorkflowExecutionV2QueryKey({ workflowId, executionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>> = ({ signal }) =>
    cancelWorkflowExecutionV2({ workflowId, executionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined && executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CancelWorkflowExecutionV2QueryResult = NonNullable<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>>;
export type CancelWorkflowExecutionV2QueryError = ErrorType<TLHTTPValidationError>;

export function useCancelWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelWorkflowExecutionV2PathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>,
          TError,
          Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>,
          TError,
          Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel Workflow Execution V2
 */

export function useCancelWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId }: CancelWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCancelWorkflowExecutionV2QueryOptions({ workflowId, executionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Workflow Execution V2
 */
export const prefetchCancelWorkflowExecutionV2Query = async <
  TData = Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, executionId }: CancelWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCancelWorkflowExecutionV2QueryOptions({ workflowId, executionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Cancel Workflow Execution V2
 */
export const invalidateCancelWorkflowExecutionV2 = async (
  queryClient: QueryClient,
  { workflowId, executionId }: CancelWorkflowExecutionV2PathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCancelWorkflowExecutionV2QueryKey({ workflowId, executionId }) },
    options,
  );

  return queryClient;
};

/**
 * Bundle all node outputs of a finished execution (results.md + output files) into a zip.
 * @summary Export Workflow Execution Outputs V2
 */
export const exportWorkflowExecutionOutputsV2 = (
  { workflowId, executionId }: ExportWorkflowExecutionOutputsV2PathParameters,
  params?: ExportWorkflowExecutionOutputsV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDownloadUrlResponse>(
    { url: `/api/v2/workflows/${workflowId}/executions/${executionId}/export`, method: 'POST', params, signal },
    options,
  );
};

export const getExportWorkflowExecutionOutputsV2QueryKey = (
  { workflowId, executionId }: ExportWorkflowExecutionOutputsV2PathParameters,
  params?: ExportWorkflowExecutionOutputsV2Params,
) => {
  return [
    'POST',
    'api',
    'v2',
    'workflows',
    workflowId,
    'executions',
    executionId,
    'export',
    ...(params ? [params] : []),
  ] as const;
};

export const getExportWorkflowExecutionOutputsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId }: ExportWorkflowExecutionOutputsV2PathParameters,
  params?: ExportWorkflowExecutionOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getExportWorkflowExecutionOutputsV2QueryKey({ workflowId, executionId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>> = ({ signal }) =>
    exportWorkflowExecutionOutputsV2({ workflowId, executionId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined && executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExportWorkflowExecutionOutputsV2QueryResult = NonNullable<
  Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>
>;
export type ExportWorkflowExecutionOutputsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useExportWorkflowExecutionOutputsV2<
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportWorkflowExecutionOutputsV2PathParameters,
  params: undefined | ExportWorkflowExecutionOutputsV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>,
          TError,
          Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportWorkflowExecutionOutputsV2<
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportWorkflowExecutionOutputsV2PathParameters,
  params?: ExportWorkflowExecutionOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>,
          TError,
          Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportWorkflowExecutionOutputsV2<
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportWorkflowExecutionOutputsV2PathParameters,
  params?: ExportWorkflowExecutionOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Workflow Execution Outputs V2
 */

export function useExportWorkflowExecutionOutputsV2<
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId }: ExportWorkflowExecutionOutputsV2PathParameters,
  params?: ExportWorkflowExecutionOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportWorkflowExecutionOutputsV2QueryOptions({ workflowId, executionId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Workflow Execution Outputs V2
 */
export const prefetchExportWorkflowExecutionOutputsV2Query = async <
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, executionId }: ExportWorkflowExecutionOutputsV2PathParameters,
  params?: ExportWorkflowExecutionOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionOutputsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportWorkflowExecutionOutputsV2QueryOptions({ workflowId, executionId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Workflow Execution Outputs V2
 */
export const invalidateExportWorkflowExecutionOutputsV2 = async (
  queryClient: QueryClient,
  { workflowId, executionId }: ExportWorkflowExecutionOutputsV2PathParameters,
  params?: ExportWorkflowExecutionOutputsV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExportWorkflowExecutionOutputsV2QueryKey({ workflowId, executionId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Submit Workflow Approval Decision V2
 */
export const submitWorkflowApprovalDecisionV2 = (
  { workflowId, executionId, nodeId }: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest: BodyType<TLApprovalDecisionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowExecution>(
    {
      url: `/api/v2/workflows/${workflowId}/executions/${executionId}/nodes/${nodeId}/decision`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLApprovalDecisionRequest,
      signal,
    },
    options,
  );
};

export const getSubmitWorkflowApprovalDecisionV2QueryKey = (
  { workflowId, executionId, nodeId }: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest?: BodyType<TLApprovalDecisionRequest>,
) => {
  return [
    'POST',
    'api',
    'v2',
    'workflows',
    workflowId,
    'executions',
    executionId,
    'nodes',
    nodeId,
    'decision',
    tLApprovalDecisionRequest,
  ] as const;
};

export const getSubmitWorkflowApprovalDecisionV2QueryOptions = <
  TData = Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId, nodeId }: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest: BodyType<TLApprovalDecisionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getSubmitWorkflowApprovalDecisionV2QueryKey({ workflowId, executionId, nodeId }, tLApprovalDecisionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>> = ({ signal }) =>
    submitWorkflowApprovalDecisionV2(
      { workflowId, executionId, nodeId },
      tLApprovalDecisionRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled:
      workflowId !== null &&
      workflowId !== undefined &&
      executionId !== null &&
      executionId !== undefined &&
      nodeId !== null &&
      nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SubmitWorkflowApprovalDecisionV2QueryResult = NonNullable<
  Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>
>;
export type SubmitWorkflowApprovalDecisionV2QueryError = ErrorType<TLHTTPValidationError>;

export function useSubmitWorkflowApprovalDecisionV2<
  TData = Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest: BodyType<TLApprovalDecisionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>,
          TError,
          Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSubmitWorkflowApprovalDecisionV2<
  TData = Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest: BodyType<TLApprovalDecisionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>,
          TError,
          Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSubmitWorkflowApprovalDecisionV2<
  TData = Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest: BodyType<TLApprovalDecisionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Submit Workflow Approval Decision V2
 */

export function useSubmitWorkflowApprovalDecisionV2<
  TData = Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId, nodeId }: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest: BodyType<TLApprovalDecisionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSubmitWorkflowApprovalDecisionV2QueryOptions(
    { workflowId, executionId, nodeId },
    tLApprovalDecisionRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Submit Workflow Approval Decision V2
 */
export const prefetchSubmitWorkflowApprovalDecisionV2Query = async <
  TData = Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, executionId, nodeId }: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest: BodyType<TLApprovalDecisionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitWorkflowApprovalDecisionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSubmitWorkflowApprovalDecisionV2QueryOptions(
    { workflowId, executionId, nodeId },
    tLApprovalDecisionRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Submit Workflow Approval Decision V2
 */
export const invalidateSubmitWorkflowApprovalDecisionV2 = async (
  queryClient: QueryClient,
  { workflowId, executionId, nodeId }: SubmitWorkflowApprovalDecisionV2PathParameters,
  tLApprovalDecisionRequest: BodyType<TLApprovalDecisionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getSubmitWorkflowApprovalDecisionV2QueryKey(
        { workflowId, executionId, nodeId },
        tLApprovalDecisionRequest,
      ),
    },
    options,
  );

  return queryClient;
};

/**
 * Bundle one finished node's outputs (results.md + output files) into a zip.
 * @summary Export Workflow Execution Node Outputs V2
 */
export const exportWorkflowExecutionNodeOutputsV2 = (
  { workflowId, executionId, nodeId }: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params?: ExportWorkflowExecutionNodeOutputsV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDownloadUrlResponse>(
    {
      url: `/api/v2/workflows/${workflowId}/executions/${executionId}/nodes/${nodeId}/export`,
      method: 'POST',
      params,
      signal,
    },
    options,
  );
};

export const getExportWorkflowExecutionNodeOutputsV2QueryKey = (
  { workflowId, executionId, nodeId }: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params?: ExportWorkflowExecutionNodeOutputsV2Params,
) => {
  return [
    'POST',
    'api',
    'v2',
    'workflows',
    workflowId,
    'executions',
    executionId,
    'nodes',
    nodeId,
    'export',
    ...(params ? [params] : []),
  ] as const;
};

export const getExportWorkflowExecutionNodeOutputsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId, nodeId }: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params?: ExportWorkflowExecutionNodeOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getExportWorkflowExecutionNodeOutputsV2QueryKey({ workflowId, executionId, nodeId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>> = ({ signal }) =>
    exportWorkflowExecutionNodeOutputsV2({ workflowId, executionId, nodeId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      workflowId !== null &&
      workflowId !== undefined &&
      executionId !== null &&
      executionId !== undefined &&
      nodeId !== null &&
      nodeId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExportWorkflowExecutionNodeOutputsV2QueryResult = NonNullable<
  Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>
>;
export type ExportWorkflowExecutionNodeOutputsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useExportWorkflowExecutionNodeOutputsV2<
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params: undefined | ExportWorkflowExecutionNodeOutputsV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>,
          TError,
          Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportWorkflowExecutionNodeOutputsV2<
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params?: ExportWorkflowExecutionNodeOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>,
          TError,
          Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportWorkflowExecutionNodeOutputsV2<
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params?: ExportWorkflowExecutionNodeOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Workflow Execution Node Outputs V2
 */

export function useExportWorkflowExecutionNodeOutputsV2<
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId, nodeId }: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params?: ExportWorkflowExecutionNodeOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportWorkflowExecutionNodeOutputsV2QueryOptions(
    { workflowId, executionId, nodeId },
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Workflow Execution Node Outputs V2
 */
export const prefetchExportWorkflowExecutionNodeOutputsV2Query = async <
  TData = Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, executionId, nodeId }: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params?: ExportWorkflowExecutionNodeOutputsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportWorkflowExecutionNodeOutputsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportWorkflowExecutionNodeOutputsV2QueryOptions(
    { workflowId, executionId, nodeId },
    params,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Workflow Execution Node Outputs V2
 */
export const invalidateExportWorkflowExecutionNodeOutputsV2 = async (
  queryClient: QueryClient,
  { workflowId, executionId, nodeId }: ExportWorkflowExecutionNodeOutputsV2PathParameters,
  params?: ExportWorkflowExecutionNodeOutputsV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExportWorkflowExecutionNodeOutputsV2QueryKey({ workflowId, executionId, nodeId }, params) },
    options,
  );

  return queryClient;
};

/**
 * Get a presigned download URL for an input or output file from a workflow node execution.
 * @summary Download Execution File V2
 */
export const downloadExecutionFileV2 = (
  { workflowId, executionId, nodeId, programId }: DownloadExecutionFileV2PathParameters,
  params?: DownloadExecutionFileV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDownloadUrlResponse>(
    {
      url: `/api/v2/workflows/${workflowId}/executions/${executionId}/nodes/${nodeId}/files/${programId}/download`,
      method: 'GET',
      params,
      signal,
    },
    options,
  );
};

export const getDownloadExecutionFileV2QueryKey = (
  { workflowId, executionId, nodeId, programId }: DownloadExecutionFileV2PathParameters,
  params?: DownloadExecutionFileV2Params,
) => {
  return [
    'api',
    'v2',
    'workflows',
    workflowId,
    'executions',
    executionId,
    'nodes',
    nodeId,
    'files',
    programId,
    'download',
    ...(params ? [params] : []),
  ] as const;
};

export const getDownloadExecutionFileV2QueryOptions = <
  TData = Awaited<ReturnType<typeof downloadExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId, nodeId, programId }: DownloadExecutionFileV2PathParameters,
  params?: DownloadExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadExecutionFileV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getDownloadExecutionFileV2QueryKey({ workflowId, executionId, nodeId, programId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadExecutionFileV2>>> = ({ signal }) =>
    downloadExecutionFileV2({ workflowId, executionId, nodeId, programId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      workflowId !== null &&
      workflowId !== undefined &&
      executionId !== null &&
      executionId !== undefined &&
      nodeId !== null &&
      nodeId !== undefined &&
      programId !== null &&
      programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof downloadExecutionFileV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DownloadExecutionFileV2QueryResult = NonNullable<Awaited<ReturnType<typeof downloadExecutionFileV2>>>;
export type DownloadExecutionFileV2QueryError = ErrorType<TLHTTPValidationError>;

export function useDownloadExecutionFileV2<
  TData = Awaited<ReturnType<typeof downloadExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DownloadExecutionFileV2PathParameters,
  params: undefined | DownloadExecutionFileV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadExecutionFileV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadExecutionFileV2>>,
          TError,
          Awaited<ReturnType<typeof downloadExecutionFileV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDownloadExecutionFileV2<
  TData = Awaited<ReturnType<typeof downloadExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DownloadExecutionFileV2PathParameters,
  params?: DownloadExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadExecutionFileV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadExecutionFileV2>>,
          TError,
          Awaited<ReturnType<typeof downloadExecutionFileV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDownloadExecutionFileV2<
  TData = Awaited<ReturnType<typeof downloadExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DownloadExecutionFileV2PathParameters,
  params?: DownloadExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadExecutionFileV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Download Execution File V2
 */

export function useDownloadExecutionFileV2<
  TData = Awaited<ReturnType<typeof downloadExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId, nodeId, programId }: DownloadExecutionFileV2PathParameters,
  params?: DownloadExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadExecutionFileV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDownloadExecutionFileV2QueryOptions(
    { workflowId, executionId, nodeId, programId },
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Download Execution File V2
 */
export const prefetchDownloadExecutionFileV2Query = async <
  TData = Awaited<ReturnType<typeof downloadExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, executionId, nodeId, programId }: DownloadExecutionFileV2PathParameters,
  params?: DownloadExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadExecutionFileV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDownloadExecutionFileV2QueryOptions(
    { workflowId, executionId, nodeId, programId },
    params,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Download Execution File V2
 */
export const invalidateDownloadExecutionFileV2 = async (
  queryClient: QueryClient,
  { workflowId, executionId, nodeId, programId }: DownloadExecutionFileV2PathParameters,
  params?: DownloadExecutionFileV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDownloadExecutionFileV2QueryKey({ workflowId, executionId, nodeId, programId }, params) },
    options,
  );

  return queryClient;
};

/**
 * Preview an input or output file from a workflow node execution.
 * @summary Preview Execution File V2
 */
export const previewExecutionFileV2 = (
  { workflowId, executionId, nodeId, programId }: PreviewExecutionFileV2PathParameters,
  params?: PreviewExecutionFileV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPreviewResponse>(
    {
      url: `/api/v2/workflows/${workflowId}/executions/${executionId}/nodes/${nodeId}/files/${programId}/preview`,
      method: 'GET',
      params,
      signal,
    },
    options,
  );
};

export const getPreviewExecutionFileV2QueryKey = (
  { workflowId, executionId, nodeId, programId }: PreviewExecutionFileV2PathParameters,
  params?: PreviewExecutionFileV2Params,
) => {
  return [
    'api',
    'v2',
    'workflows',
    workflowId,
    'executions',
    executionId,
    'nodes',
    nodeId,
    'files',
    programId,
    'preview',
    ...(params ? [params] : []),
  ] as const;
};

export const getPreviewExecutionFileV2QueryOptions = <
  TData = Awaited<ReturnType<typeof previewExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId, nodeId, programId }: PreviewExecutionFileV2PathParameters,
  params?: PreviewExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewExecutionFileV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getPreviewExecutionFileV2QueryKey({ workflowId, executionId, nodeId, programId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof previewExecutionFileV2>>> = ({ signal }) =>
    previewExecutionFileV2({ workflowId, executionId, nodeId, programId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      workflowId !== null &&
      workflowId !== undefined &&
      executionId !== null &&
      executionId !== undefined &&
      nodeId !== null &&
      nodeId !== undefined &&
      programId !== null &&
      programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof previewExecutionFileV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PreviewExecutionFileV2QueryResult = NonNullable<Awaited<ReturnType<typeof previewExecutionFileV2>>>;
export type PreviewExecutionFileV2QueryError = ErrorType<TLHTTPValidationError>;

export function usePreviewExecutionFileV2<
  TData = Awaited<ReturnType<typeof previewExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PreviewExecutionFileV2PathParameters,
  params: undefined | PreviewExecutionFileV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewExecutionFileV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewExecutionFileV2>>,
          TError,
          Awaited<ReturnType<typeof previewExecutionFileV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewExecutionFileV2<
  TData = Awaited<ReturnType<typeof previewExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PreviewExecutionFileV2PathParameters,
  params?: PreviewExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewExecutionFileV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewExecutionFileV2>>,
          TError,
          Awaited<ReturnType<typeof previewExecutionFileV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewExecutionFileV2<
  TData = Awaited<ReturnType<typeof previewExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PreviewExecutionFileV2PathParameters,
  params?: PreviewExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewExecutionFileV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview Execution File V2
 */

export function usePreviewExecutionFileV2<
  TData = Awaited<ReturnType<typeof previewExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId, nodeId, programId }: PreviewExecutionFileV2PathParameters,
  params?: PreviewExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewExecutionFileV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPreviewExecutionFileV2QueryOptions(
    { workflowId, executionId, nodeId, programId },
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview Execution File V2
 */
export const prefetchPreviewExecutionFileV2Query = async <
  TData = Awaited<ReturnType<typeof previewExecutionFileV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, executionId, nodeId, programId }: PreviewExecutionFileV2PathParameters,
  params?: PreviewExecutionFileV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewExecutionFileV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPreviewExecutionFileV2QueryOptions(
    { workflowId, executionId, nodeId, programId },
    params,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Preview Execution File V2
 */
export const invalidatePreviewExecutionFileV2 = async (
  queryClient: QueryClient,
  { workflowId, executionId, nodeId, programId }: PreviewExecutionFileV2PathParameters,
  params?: PreviewExecutionFileV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPreviewExecutionFileV2QueryKey({ workflowId, executionId, nodeId, programId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Stream Workflow Execution V2
 */
export const streamWorkflowExecutionV2 = (
  { workflowId, executionId }: StreamWorkflowExecutionV2PathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>(
    { url: `/api/v2/workflows/${workflowId}/executions/${executionId}/stream`, method: 'GET', signal },
    options,
  );
};

export const getStreamWorkflowExecutionV2QueryKey = ({
  workflowId,
  executionId,
}: StreamWorkflowExecutionV2PathParameters) => {
  return ['api', 'v2', 'workflows', workflowId, 'executions', executionId, 'stream'] as const;
};

export const getStreamWorkflowExecutionV2QueryOptions = <
  TData = Awaited<ReturnType<typeof streamWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId }: StreamWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStreamWorkflowExecutionV2QueryKey({ workflowId, executionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>> = ({ signal }) =>
    streamWorkflowExecutionV2({ workflowId, executionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined && executionId !== null && executionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type StreamWorkflowExecutionV2QueryResult = NonNullable<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>>;
export type StreamWorkflowExecutionV2QueryError = ErrorType<TLHTTPValidationError>;

export function useStreamWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof streamWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamWorkflowExecutionV2PathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamWorkflowExecutionV2>>,
          TError,
          Awaited<ReturnType<typeof streamWorkflowExecutionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStreamWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof streamWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamWorkflowExecutionV2>>,
          TError,
          Awaited<ReturnType<typeof streamWorkflowExecutionV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStreamWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof streamWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Stream Workflow Execution V2
 */

export function useStreamWorkflowExecutionV2<
  TData = Awaited<ReturnType<typeof streamWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId, executionId }: StreamWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStreamWorkflowExecutionV2QueryOptions({ workflowId, executionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Stream Workflow Execution V2
 */
export const prefetchStreamWorkflowExecutionV2Query = async <
  TData = Awaited<ReturnType<typeof streamWorkflowExecutionV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId, executionId }: StreamWorkflowExecutionV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamWorkflowExecutionV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStreamWorkflowExecutionV2QueryOptions({ workflowId, executionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Stream Workflow Execution V2
 */
export const invalidateStreamWorkflowExecutionV2 = async (
  queryClient: QueryClient,
  { workflowId, executionId }: StreamWorkflowExecutionV2PathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getStreamWorkflowExecutionV2QueryKey({ workflowId, executionId }) },
    options,
  );

  return queryClient;
};
